import * as Parse from 'parse'
import { BehaviorSubject } from 'rxjs'
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators'
import { Objects } from '../../../../../server/src/shared-with-client/object-definitions'
import { SharedFuture } from '../shared-future'

interface FindParams {
    user: { id: string }
}

class UserRoles {
    private roles: { [roleName: string]: boolean } = {}

    constructor(private roleService: RoleService) {}
    addRole(name: Objects.Role.Any, value: boolean) {
        this.roles[name] = value
    }
    hasRole(name: Objects.Role.Any) {
        return !!this.roles[name]
    }
    clear() {
        this.roleService.find.resetAll()
        this.roles = {}
    }
    requiresUpdate() {
        return !this.hasRoles()
    }

    hasRoles() {
        return Object.keys(this.roles).length > 0
    }
}

export class RoleService {
    readonly userRoles = new UserRoles(this)

    private _roles$ = new BehaviorSubject<Objects.Role.Any[]>([])
    roles$ = this._roles$.pipe(
        map((roles) => {
            return roles.sort()
        }),
        distinctUntilChanged((rolesA, rolesB) => {
            return JSON.stringify(rolesA) === JSON.stringify(rolesB)
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    )

    readonly find = new SharedFuture<Objects.Role.Parse[], FindParams>(
        (args) => this.findHandler(args),
        (args) => {
            const { user } = args
            return user ? `${user.id}` : ''
        },
        { time: { ttl: 60000 } }
    )

    constructor() {}

    clearRoles() {
        this.userRoles.clear()
        this._roles$.next([])
    }

    /** @deprecated */
    async doesUserHavePermission(roleName: Objects.Role.Any): Promise<boolean> {
        try {
            const user = Parse.User.current()
            if (user === null || user === undefined) {
                return false
            }

            if (this.userRoles.requiresUpdate()) {
                await this.find.toPromise({ user })
            }
        } catch (err) {
            return false
        }

        const hasRole = this.userRoles.hasRole(roleName)

        return hasRole
    }

    private async findHandler({ user }: FindParams): Promise<Objects.Role.Parse[]> {
        if (!user) {
            return []
        }

        const roles = await new Parse.Query<Objects.Role.Parse>(Parse.Role).equalTo('isGeneric', true).select('name').find()

        for (const role of roles) {
            const name = role.get('name')
            if (!name) {
                continue
            }
            this.userRoles.addRole(name, true)
        }

        this._roles$.next(roles.map((role) => role.get('name')))

        return roles
    }
}
